import { mudTextToHtml, normalizeNewlines } from "./color";
import { ambientLabel, applyAmbientToDom, extractAmbient } from "./ambient";
import {
  CLIENT_BUILD,
  type ClientPrefs,
  aliasesEditorText,
  defaultWsUrl,
  ensureProfile,
  expandAlias,
  exportPrefsJson,
  getActiveChar,
  importPrefsJson,
  listProfiles,
  loadPrefs,
  pushHistory,
  savePrefs,
  textToAliases,
  textToTriggers,
  triggersEditorText,
} from "./prefs";
import { appendLog, clearLog, downloadLog, getLogSize, setLoggingEnabled } from "./sessionLog";
import { unlockAudio, playBeep } from "./sound";
import { applyTriggerSounds, evaluateTriggers } from "./triggers";

const MAX_STREAM_CHARS = 400_000;
const RECONNECT_MAX_MS = 30_000;
const RECONNECT_MAX_TRIES = 12;

type ConnState = "disconnected" | "connecting" | "connected" | "reconnecting";

function el<T extends HTMLElement>(id: string): T {
  const node = document.getElementById(id);
  if (!node) throw new Error(`Missing #${id}`);
  return node as T;
}

const els = {
  output: el<HTMLElement>("output"),
  stream: el<HTMLElement>("stream"),
  banner: el<HTMLElement>("banner"),
  form: el<HTMLFormElement>("input-form"),
  command: el<HTMLInputElement>("command"),
  btnConnect: el<HTMLButtonElement>("btn-connect"),
  btnDisconnect: el<HTMLButtonElement>("btn-disconnect"),
  btnSend: el<HTMLButtonElement>("btn-send"),
  btnSettings: el<HTMLButtonElement>("btn-settings"),
  btnAliases: el<HTMLButtonElement>("btn-aliases"),
  btnTriggers: el<HTMLButtonElement>("btn-triggers"),
  btnClear: el<HTMLButtonElement>("btn-clear"),
  btnJump: el<HTMLButtonElement>("btn-jump"),
  passwordMode: el<HTMLInputElement>("password-mode"),
  status: el<HTMLElement>("status"),
  profileLabel: el<HTMLElement>("profile-label"),
  buildStamp: el<HTMLElement>("build-stamp"),
  logIndicator: el<HTMLElement>("log-indicator"),
  ambientChip: el<HTMLElement>("ambient-chip"),

  settingsDialog: el<HTMLDialogElement>("settings-dialog"),
  settingsForm: el<HTMLFormElement>("settings-form"),
  setWsUrl: el<HTMLInputElement>("set-ws-url"),
  setAutoReconnect: el<HTMLInputElement>("set-auto-reconnect"),
  setFontSize: el<HTMLSelectElement>("set-font-size"),
  setHighContrast: el<HTMLInputElement>("set-high-contrast"),
  setTimestamps: el<HTMLInputElement>("set-timestamps"),
  setExitHotkeys: el<HTMLInputElement>("set-exit-hotkeys"),
  setLogging: el<HTMLInputElement>("set-logging"),
  logSize: el<HTMLElement>("log-size"),
  btnDownloadLog: el<HTMLButtonElement>("btn-download-log"),
  btnClearLog: el<HTMLButtonElement>("btn-clear-log"),
  setProfile: el<HTMLSelectElement>("set-profile"),
  setNewProfile: el<HTMLInputElement>("set-new-profile"),
  btnAddProfile: el<HTMLButtonElement>("btn-add-profile"),
  btnExport: el<HTMLButtonElement>("btn-export"),
  btnImport: el<HTMLButtonElement>("btn-import"),
  importFile: el<HTMLInputElement>("import-file"),

  aliasesDialog: el<HTMLDialogElement>("aliases-dialog"),
  aliasesForm: el<HTMLFormElement>("aliases-form"),
  aliasesProfileName: el<HTMLElement>("aliases-profile-name"),
  setAliases: el<HTMLTextAreaElement>("set-aliases"),
  setGlobalAliases: el<HTMLTextAreaElement>("set-global-aliases"),

  triggersDialog: el<HTMLDialogElement>("triggers-dialog"),
  triggersForm: el<HTMLFormElement>("triggers-form"),
  triggersProfileName: el<HTMLElement>("triggers-profile-name"),
  setTriggers: el<HTMLTextAreaElement>("set-triggers"),
  setGlobalTriggers: el<HTMLTextAreaElement>("set-global-triggers"),
  btnTestBeep: el<HTMLButtonElement>("btn-test-beep"),
};

let prefs: ClientPrefs = loadPrefs();
savePrefs(prefs);

let socket: WebSocket | null = null;
let historyIndex = -1;
let draft = "";
let recentPlain = "";
let stickToBottom = true;
let userClosed = false;
let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
let reconnectAttempt = 0;
let connState: ConnState = "disconnected";
/** Avoid trigger loops when we inject commands from triggers. */
let triggerDepth = 0;

const ARROW_EXITS: Record<string, string> = {
  ArrowUp: "north",
  ArrowDown: "south",
  ArrowRight: "east",
  ArrowLeft: "west",
};

// --- prefs display ---

function applyDisplayPrefs(): void {
  document.documentElement.dataset.font = prefs.fontSize;
  document.documentElement.classList.toggle("high-contrast", prefs.highContrast);
  els.profileLabel.textContent = `Profile: ${prefs.activeProfile}`;
  els.buildStamp.textContent = `client ${CLIENT_BUILD}`;
  setLoggingEnabled(prefs.loggingEnabled);
  els.logIndicator.textContent = prefs.loggingEnabled ? "log on" : "log off";
  els.logIndicator.className = prefs.loggingEnabled ? "log-on" : "log-off";
  updateLogSizeLabel();
}

function persist(): void {
  savePrefs(prefs);
}

function historyList(): string[] {
  return getActiveChar(prefs).history;
}

function updateLogSizeLabel(): void {
  els.logSize.textContent = String(getLogSize());
}

// --- UI helpers ---

function setStatus(state: ConnState): void {
  connState = state;
  els.status.textContent = state;
  els.status.className = `status ${state}`;
  const live = state === "connected";
  const busy = state === "connecting" || state === "reconnecting";
  els.command.disabled = !live;
  els.btnSend.disabled = !live;
  els.btnConnect.disabled = live || busy;
  els.btnDisconnect.disabled = state === "disconnected";
}

function showBanner(msg: string, kind: "info" | "warn" | "error" = "info"): void {
  els.banner.textContent = msg;
  els.banner.className = `banner ${kind}`;
  els.banner.classList.remove("hidden");
}

function hideBanner(): void {
  els.banner.classList.add("hidden");
  els.banner.textContent = "";
}

function isNearBottom(): boolean {
  const el = els.output;
  return el.scrollHeight - el.scrollTop - el.clientHeight < 80;
}

function scrollBottom(force = false): void {
  if (force || stickToBottom) {
    els.output.scrollTop = els.output.scrollHeight;
    stickToBottom = true;
    els.btnJump.classList.add("hidden");
  }
}

function updateJumpButton(): void {
  stickToBottom = isNearBottom();
  els.btnJump.classList.toggle("hidden", stickToBottom);
}

function trimStream(): void {
  while (els.stream.textContent && els.stream.textContent.length > MAX_STREAM_CHARS) {
    const first = els.stream.firstChild;
    if (!first) break;
    els.stream.removeChild(first);
  }
}

function stampPrefix(): string {
  if (!prefs.showTimestamps) return "";
  return `[${new Date().toLocaleTimeString(undefined, { hour12: false })}] `;
}

function appendMudHtml(html: string): void {
  if (!html) return;
  const wasSticky = stickToBottom;
  els.stream.insertAdjacentHTML("beforeend", html);
  trimStream();
  if (wasSticky) scrollBottom(true);
  else updateJumpButton();
}

function sys(msg: string): void {
  const div = document.createElement("div");
  div.className = "note sys";
  div.textContent = `${stampPrefix()}${msg}`;
  const wasSticky = stickToBottom;
  els.stream.appendChild(div);
  if (wasSticky) scrollBottom(true);
  else updateJumpButton();
}

function echoLine(text: string): void {
  const div = document.createElement("div");
  div.className = "note echo";
  div.textContent = text;
  const wasSticky = stickToBottom;
  els.stream.appendChild(div);
  if (wasSticky) scrollBottom(true);
}

function applyPasswordMode(): void {
  els.command.type = els.passwordMode.checked ? "password" : "text";
}

function maybeAutoPasswordMode(plainChunk: string): void {
  recentPlain = (recentPlain + plainChunk).slice(-400);
  if (/password/i.test(recentPlain) && /:\s*$/m.test(recentPlain.slice(-80))) {
    if (!els.passwordMode.checked) {
      els.passwordMode.checked = true;
      applyPasswordMode();
    }
  }
}

function decodeMessage(data: unknown): string {
  if (typeof data === "string") return data;
  if (data instanceof ArrayBuffer) {
    return new TextDecoder("latin1").decode(data);
  }
  if (ArrayBuffer.isView(data)) {
    return new TextDecoder("latin1").decode(data as ArrayBufferView);
  }
  return String(data);
}

function clearReconnectTimer(): void {
  if (reconnectTimer) {
    clearTimeout(reconnectTimer);
    reconnectTimer = null;
  }
}

function scheduleReconnect(): void {
  if (!prefs.autoReconnect || userClosed) return;
  if (reconnectAttempt >= RECONNECT_MAX_TRIES) {
    showBanner("Auto-reconnect gave up. Click Connect to try again.", "warn");
    setStatus("disconnected");
    return;
  }
  const delay = Math.min(1000 * 2 ** reconnectAttempt, RECONNECT_MAX_MS);
  reconnectAttempt++;
  setStatus("reconnecting");
  showBanner(`Reconnecting in ${Math.round(delay / 1000)}s (attempt ${reconnectAttempt})…`, "warn");
  clearReconnectTimer();
  reconnectTimer = setTimeout(() => connect(true), delay);
}

// --- send / triggers ---

function sendLineClean(raw: string, opts?: { fromTrigger?: boolean }): void {
  if (!socket || socket.readyState !== WebSocket.OPEN) {
    if (!opts?.fromTrigger) sys("Not connected.");
    return;
  }

  const typed = raw.replace(/\r?\n$/, "");
  const password = els.passwordMode.checked;
  let toSend = typed;

  if (!password && !opts?.fromTrigger) {
    const { line: expanded, expanded: did, from } = expandAlias(typed, prefs);
    if (did) {
      echoLine(`» ${from} → ${expanded}`);
      toSend = expanded;
    } else if (typed.length > 0) {
      echoLine(`» ${typed}`);
    }
    if (typed.length > 0) {
      pushHistory(prefs, typed);
      persist();
    }
  } else if (password && !opts?.fromTrigger) {
    echoLine("» ********");
  } else if (opts?.fromTrigger && typed.length > 0) {
    echoLine(`» [trigger] ${typed}`);
  }

  socket.send(toSend);

  if (!opts?.fromTrigger) {
    historyIndex = -1;
    draft = "";
    els.command.value = "";
  }
  stickToBottom = true;
  scrollBottom(true);
}

function processTriggers(plainChunk: string): void {
  if (triggerDepth > 0) return; // don't chain off trigger-caused output in same stack
  if (els.passwordMode.checked) return; // avoid noise during login password

  const fires = evaluateTriggers(plainChunk, prefs);
  if (fires.length === 0) return;

  applyTriggerSounds(fires);

  for (const f of fires) {
    if (!f.command) continue;
    triggerDepth++;
    try {
      // Triggers send literal commands (no alias expand) to keep behavior predictable
      sendLineClean(f.command, { fromTrigger: true });
    } finally {
      triggerDepth--;
    }
  }
}

// --- connection ---

function connect(isRetry = false): void {
  if (socket && (socket.readyState === WebSocket.OPEN || socket.readyState === WebSocket.CONNECTING)) {
    return;
  }

  unlockAudio();

  const url = prefs.wsUrl.trim() || defaultWsUrl();
  prefs.wsUrl = url;
  persist();

  userClosed = false;
  clearReconnectTimer();
  setStatus(isRetry ? "reconnecting" : "connecting");
  if (!isRetry) {
    hideBanner();
    sys(`Connecting to ${url} …`);
  }

  try {
    socket = new WebSocket(url);
  } catch (err) {
    sys(`Failed to open WebSocket: ${(err as Error).message}`);
    showBanner("Could not open WebSocket. Check Settings → WebSocket URL.", "error");
    setStatus("disconnected");
    scheduleReconnect();
    return;
  }

  socket.binaryType = "arraybuffer";

  socket.addEventListener("open", () => {
    reconnectAttempt = 0;
    setStatus("connected");
    hideBanner();
    sys(isRetry ? "Reconnected." : "Connected.");
    showBanner("Connected", "info");
    setTimeout(() => {
      if (connState === "connected") hideBanner();
    }, 2000);
    els.command.focus();
    /* Opt into mud ambient side-channel (telnet never sends this). */
    try {
      socket!.send("#MDCLIENT 1");
    } catch {
      /* ignore */
    }
  });

  socket.addEventListener("message", (ev) => {
    const raw = decodeMessage(ev.data);
    const { display, ambient, hello } = extractAmbient(raw);
    if (ambient) {
      applyAmbientToDom(ambient);
      els.ambientChip.textContent = ambientLabel(ambient);
      els.ambientChip.classList.add("active");
      els.ambientChip.title = JSON.stringify(ambient);
    } else if (hello) {
      els.ambientChip.textContent = "web client linked";
      els.ambientChip.classList.add("active");
    }

    const plain = normalizeNewlines(display);
    if (!plain && !display) return;

    maybeAutoPasswordMode(plain);
    appendLog(plain);
    if (prefs.loggingEnabled) updateLogSizeLabel();
    if (display) appendMudHtml(mudTextToHtml(display));
    processTriggers(plain);
  });

  socket.addEventListener("close", (ev) => {
    socket = null;
    const reason = ev.reason || (ev.code ? `code ${ev.code}` : "");
    if (userClosed) {
      sys(`Disconnected${reason ? ` (${reason})` : ""}.`);
      setStatus("disconnected");
      hideBanner();
      return;
    }
    sys(`Connection closed${reason ? ` (${reason})` : ""}.`);
    if (prefs.autoReconnect) scheduleReconnect();
    else {
      setStatus("disconnected");
      showBanner("Disconnected. Click Connect to try again.", "warn");
    }
  });

  socket.addEventListener("error", () => {
    if (!isRetry) sys("WebSocket error — is mdhoria-gateway running?");
  });
}

function disconnect(): void {
  userClosed = true;
  clearReconnectTimer();
  reconnectAttempt = 0;
  if (socket) {
    socket.close(1000, "client disconnect");
    socket = null;
  }
  setStatus("disconnected");
  hideBanner();
  sys("Disconnected.");
}

// --- dialogs ---

function fillProfileSelect(): void {
  const names = listProfiles(prefs);
  els.setProfile.innerHTML = "";
  for (const n of names) {
    const opt = document.createElement("option");
    opt.value = n;
    opt.textContent = n;
    if (n === prefs.activeProfile) opt.selected = true;
    els.setProfile.appendChild(opt);
  }
}

function openSettings(): void {
  unlockAudio();
  els.setWsUrl.value = prefs.wsUrl;
  els.setAutoReconnect.checked = prefs.autoReconnect;
  els.setFontSize.value = prefs.fontSize;
  els.setHighContrast.checked = prefs.highContrast;
  els.setTimestamps.checked = prefs.showTimestamps;
  els.setExitHotkeys.checked = prefs.exitHotkeys;
  els.setLogging.checked = prefs.loggingEnabled;
  fillProfileSelect();
  updateLogSizeLabel();
  els.settingsDialog.showModal();
}

function saveSettings(): void {
  prefs.wsUrl = els.setWsUrl.value.trim() || defaultWsUrl();
  prefs.autoReconnect = els.setAutoReconnect.checked;
  prefs.fontSize = els.setFontSize.value as ClientPrefs["fontSize"];
  prefs.highContrast = els.setHighContrast.checked;
  prefs.showTimestamps = els.setTimestamps.checked;
  prefs.exitHotkeys = els.setExitHotkeys.checked;
  prefs.loggingEnabled = els.setLogging.checked;
  prefs.activeProfile = els.setProfile.value || prefs.activeProfile;
  ensureProfile(prefs, prefs.activeProfile);
  persist();
  applyDisplayPrefs();
  sys(`Settings saved (profile “${prefs.activeProfile}”).`);
}

function openAliases(): void {
  unlockAudio();
  els.aliasesProfileName.textContent = prefs.activeProfile;
  const ch = getActiveChar(prefs);
  // Profile box: show examples if this profile has no aliases yet
  els.setAliases.value = aliasesEditorText(ch.aliases);
  // Global usually already has n/s/e/w — show those; if empty, examples
  els.setGlobalAliases.value = aliasesEditorText(prefs.globalAliases);
  els.aliasesDialog.showModal();
}

function saveAliases(): void {
  getActiveChar(prefs).aliases = textToAliases(els.setAliases.value);
  prefs.globalAliases = textToAliases(els.setGlobalAliases.value);
  persist();
  sys("Aliases saved.");
}

function openTriggers(): void {
  unlockAudio();
  els.triggersProfileName.textContent = prefs.activeProfile;
  const ch = getActiveChar(prefs);
  els.setTriggers.value = triggersEditorText(ch.triggers ?? []);
  els.setGlobalTriggers.value = triggersEditorText(prefs.globalTriggers ?? []);
  els.triggersDialog.showModal();
}

function saveTriggers(): void {
  getActiveChar(prefs).triggers = textToTriggers(els.setTriggers.value);
  prefs.globalTriggers = textToTriggers(els.setGlobalTriggers.value);
  persist();
  sys("Triggers saved.");
}

function addProfile(): void {
  const name = els.setNewProfile.value.trim().slice(0, 32);
  if (!name) return;
  ensureProfile(prefs, name);
  prefs.activeProfile = name;
  els.setNewProfile.value = "";
  fillProfileSelect();
  persist();
  applyDisplayPrefs();
}

function exportPrefs(): void {
  const blob = new Blob([exportPrefsJson(prefs)], { type: "application/json" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = `mdhoria-client-prefs-${prefs.activeProfile}.json`;
  a.click();
  URL.revokeObjectURL(a.href);
  sys("Exported preferences (settings, aliases, triggers, history).");
}

function importPrefsFile(file: File): void {
  const reader = new FileReader();
  reader.onload = () => {
    try {
      prefs = importPrefsJson(String(reader.result ?? ""));
      persist();
      applyDisplayPrefs();
      fillProfileSelect();
      sys("Imported preferences.");
      showBanner("Preferences imported", "info");
      setTimeout(hideBanner, 2500);
    } catch (e) {
      sys(`Import failed: ${(e as Error).message}`);
      showBanner("Import failed — invalid JSON?", "error");
    }
  };
  reader.readAsText(file);
}

// --- wire up ---

applyDisplayPrefs();
setStatus("disconnected");
applyPasswordMode();
sys(
  `Web client ${CLIENT_BUILD} — production (port 6996 via gateway). Settings · Aliases · Triggers.`
);

// Unlock audio on first user gesture
document.addEventListener("pointerdown", () => unlockAudio(), { once: true });

els.output.addEventListener("scroll", () => updateJumpButton(), { passive: true });
els.btnJump.addEventListener("click", () => scrollBottom(true));
els.btnClear.addEventListener("click", () => {
  els.stream.replaceChildren();
  sys("Scrollback cleared.");
});

els.btnConnect.addEventListener("click", () => connect(false));
els.btnDisconnect.addEventListener("click", () => disconnect());
els.passwordMode.addEventListener("change", () => applyPasswordMode());

els.btnSettings.addEventListener("click", () => openSettings());
els.btnAliases.addEventListener("click", () => openAliases());
els.btnTriggers.addEventListener("click", () => openTriggers());

els.settingsForm.addEventListener("submit", (e) => {
  e.preventDefault();
  const submitter = (e as SubmitEvent).submitter as HTMLButtonElement | null;
  if (submitter?.value === "save") saveSettings();
  els.settingsDialog.close();
});

els.aliasesForm.addEventListener("submit", (e) => {
  e.preventDefault();
  const submitter = (e as SubmitEvent).submitter as HTMLButtonElement | null;
  if (submitter?.value === "save") saveAliases();
  els.aliasesDialog.close();
});

els.triggersForm.addEventListener("submit", (e) => {
  e.preventDefault();
  const submitter = (e as SubmitEvent).submitter as HTMLButtonElement | null;
  if (submitter?.value === "save") saveTriggers();
  els.triggersDialog.close();
});

els.setProfile.addEventListener("change", () => {
  prefs.activeProfile = els.setProfile.value;
  ensureProfile(prefs, prefs.activeProfile);
  persist();
  applyDisplayPrefs();
});

els.btnAddProfile.addEventListener("click", () => addProfile());
els.btnExport.addEventListener("click", () => exportPrefs());
els.btnImport.addEventListener("click", () => els.importFile.click());
els.importFile.addEventListener("change", () => {
  const f = els.importFile.files?.[0];
  if (f) importPrefsFile(f);
  els.importFile.value = "";
});

els.btnDownloadLog.addEventListener("click", () => {
  downloadLog();
  sys("Log download started.");
});
els.btnClearLog.addEventListener("click", () => {
  clearLog();
  updateLogSizeLabel();
  sys("Session log cleared.");
});

els.btnTestBeep.addEventListener("click", () => {
  unlockAudio();
  playBeep();
});

els.form.addEventListener("submit", (e) => {
  e.preventDefault();
  unlockAudio();
  sendLineClean(els.command.value);
});

els.command.addEventListener("keydown", (e) => {
  const hist = historyList();
  const empty = els.command.value === "";

  if (
    prefs.exitHotkeys &&
    empty &&
    !els.passwordMode.checked &&
    !e.ctrlKey &&
    !e.metaKey &&
    !e.altKey &&
    ARROW_EXITS[e.key]
  ) {
    e.preventDefault();
    sendLineClean(ARROW_EXITS[e.key]!);
    return;
  }

  if (e.key === "ArrowUp" && (!empty || !prefs.exitHotkeys)) {
    e.preventDefault();
    if (hist.length === 0) return;
    if (historyIndex === -1) {
      draft = els.command.value;
      historyIndex = hist.length - 1;
    } else if (historyIndex > 0) {
      historyIndex--;
    }
    els.command.value = hist[historyIndex] ?? "";
  } else if (e.key === "ArrowDown" && (!empty || !prefs.exitHotkeys)) {
    e.preventDefault();
    if (historyIndex === -1) return;
    if (historyIndex < hist.length - 1) {
      historyIndex++;
      els.command.value = hist[historyIndex] ?? "";
    } else {
      historyIndex = -1;
      els.command.value = draft;
    }
  }
});
